import React, {useContext, useEffect, useRef, useState} from "react";
import jsQR from "jsqr";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {SettingsContext} from "@/SettingsContext";
import {PointContext} from "@/PointContext";
import parse from "html-react-parser";

export default function QRCodeScanner() {
    const [isInitiated, setIsInitiated] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const params = useParams();
    const navigate = useNavigate();

    const { tourSettings } = useContext(SettingsContext);
    const pointContext = useContext(PointContext);

    const {t} = useTranslation();

    useEffect(() => {
        let stream = null;

        async function startVideo() {
            try {
                const videoElement = videoRef.current;
                const canvasElement = canvasRef.current;
                const canvasContext = canvasElement.getContext("2d", { willReadFrequently: true });

                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "environment" },
                });

                videoElement.srcObject = stream;
                videoElement.setAttribute("playsinline", true); // Required for iOS devices
                videoElement.play();
                requestAnimationFrame(scan);

                function scan() {
                    if (!isScanning) return; // Stop scanning if the user has toggled it off

                    if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
                        canvasElement.height = videoElement.videoHeight;
                        canvasElement.width = videoElement.videoWidth;
                        canvasContext.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

                        const imageData = canvasContext.getImageData(0, 0, canvasElement.width, canvasElement.height);
                        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

                        if (qrCode) {
                            console.log("scan -> qrCode", qrCode);
                            const url = qrCode.data;
                            if (url) {
                                try {
                                    const parsedUrl = new URL(url);
                                    console.log(parsedUrl);
                                    //const hostname = window.location.hostname.replace('dev.tourio.com', 'tourio.com');
                                    const allowedDomains = tourSettings.domains;
                                    allowedDomains.map(domain => domain.replace('dev.tourio.com', 'tourio.com'));
                                    console.log(allowedDomains);
                                    console.log(parsedUrl.hostname);
                                    console.log(allowedDomains.includes(parsedUrl.hostname));
                                    if (allowedDomains.includes(parsedUrl.hostname) && parsedUrl.pathname.includes("/qr/")) {
                                        // Navigate and stop scanning
                                        stopScanning();
                                        console.log("redirect to " + parsedUrl.href);
                                        console.log("pathname " + parsedUrl.pathname);
                                        console.log("QrCodeScanner navigate " + `${params.language ? `/${params.language}` : ''}${parsedUrl.pathname}`);

                                        navigate(`${params.language ? `/${params.language}` : ''}${parsedUrl.pathname}`, {replace: true});
                                        return;
                                    } else {
                                        stopScanning();
                                        console.log("Scanned QR code is not a tourio QR code.");
                                        setErrorMessage(t('camera.scan_result_wrong_domain'));
                                        return;
                                    }
                                } catch (error) {
                                    console.log("Invalid QR code scanned.");
                                    setErrorMessage(t('camera.scan_result_qr_error'));
                                    console.error(error);
                                    stopScanning();
                                }
                            }
                        }
                    }
                    if (isScanning) {
                        requestAnimationFrame(scan);
                    }
                }

            } catch (error) {
                setErrorMessage(t('camera.camera_permission_error'));
                console.error("Camera access error:", error);
                stopScanning();
            }
        }

        if (isInitiated && isScanning) {
            startVideo();
        }

        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [isScanning, isInitiated]);

    // This useEffect only runs once, when the component is mounted
    useEffect(() => {
        async function checkPermissionsAndStart() {
            console.log("checkPermissionsAndStart");
            try {
                const permissionStatus = await navigator.permissions.query({ name: "camera" });
                console.log(permissionStatus.state);
                if (permissionStatus.state === "granted") {
         //           startScanning()
                }
                if (permissionStatus.state === "denied") {
                    setErrorMessage(t('camera.camera_permission_error'));
                }
                setIsInitiated(true);
            } catch (error) {
                console.error("Error checking permissions:", error); // this happens on ios because navigator.permissions.query is not supported.
                setIsInitiated(true);
            }
        }

        checkPermissionsAndStart();
    }, []); // Empty dependency array ensures this only runs once

    const startScanning = () => {
        pointContext.setShowPointNavigation(false);
        setErrorMessage('');
        setIsScanning(true);
    }

    const stopScanning = () => {
        pointContext.setShowPointNavigation(true);
        setIsScanning(false);
    }


    return (
        <>
        {isInitiated &&
            <>
                {isScanning ? (
                    <>
                        <div className="absolute flex bottom-12 left-0 right-0 z-10 justify-center">
                            <button onClick={stopScanning}
                                    style={{"backgroundColor": `${ tourSettings.theme_color }`, "color": `${ tourSettings.theme_text_color }` }}
                                    className="py-2 px-6 rounded-full shadow-md"> { t('camera.stop_scanning')}
                            </button>
                        </div>
                        <video className={`absolute inset-0 w-full h-full object-cover`} ref={videoRef}></video>
                        <canvas ref={canvasRef} style={{display: "none"}}></canvas>
                    </>
                ) : (
                    <div className=" m-5 px-4 py-24">
                        { errorMessage ?
                            <p className={`text-red-700`} >{ parse(errorMessage) }</p>
                            : <>
                                <p className={`text-center`}>{ t('camera.scanner_description' )}</p>
                            </>
                        }

                        <button onClick={startScanning}
                                style={{"backgroundColor": `${ tourSettings.theme_color }`, "color": `${ tourSettings.theme_text_color }` }}
                                className="flex mx-auto my-8 py-2 px-6 rounded-full shadow-md"> { t('camera.start_scanning' )}
                        </button>
                    </div>
                )}
            </>
        }
        </>
    );
}
